import { useState } from 'react';
import '../ownerPages.css';
import './subscriptions.css';

const durationOptions = [
  { value: 'monthly', label: 'Monthly', priceKey: 'monthlyPrice' },
  { value: 'quarterly', label: 'Quarterly', priceKey: 'quarterlyPrice' },
  { value: 'yearly', label: 'Yearly', priceKey: 'yearlyPrice' },
  { value: 'lifetime', label: 'Lifetime', priceKey: 'lifetimePrice' },
];

const statusOptions = [
  { value: 'active', label: 'Active (visible on signup)' },
  { value: 'inactive', label: 'Inactive (hidden on signup)' },
];

function detectDuration(data) {
  if (!data) return '';
  if (Number(data.monthlyPrice) > 0) return 'monthly';
  if (Number(data.quarterlyPrice) > 0) return 'quarterly';
  if (Number(data.yearlyPrice) > 0) return 'yearly';
  if (Number(data.lifetimePrice) > 0) return 'lifetime';
  return 'monthly';
}

function priceForDuration(data, duration) {
  if (!data || !duration) return '';
  const option = durationOptions.find((d) => d.value === duration);
  if (!option) return '';
  const value = data[option.priceKey];
  return value != null && value !== '' ? String(value) : '';
}

const emptyForm = {
  name: '',
  duration: '',
  price: '',
  status: 'active',
  features: '',
};

function SaasPlanForm({ initialData, onSubmit, onCancel, submitLabel, isEdit = false }) {
  const initialDuration = initialData ? detectDuration(initialData) : '';

  const [form, setForm] = useState(() => ({
    ...emptyForm,
    ...initialData,
    duration: initialDuration,
    price: priceForDuration(initialData, initialDuration),
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'duration') {
      setForm((prev) => ({
        ...prev,
        duration: value,
        price: priceForDuration(initialData, value) || '',
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const selectedDuration = durationOptions.find((d) => d.value === form.duration);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.duration) return;

    const price = Number(form.price) || 0;
    const prices = {
      monthlyPrice: Number(initialData?.monthlyPrice) || 0,
      quarterlyPrice: Number(initialData?.quarterlyPrice) || 0,
      yearlyPrice: Number(initialData?.yearlyPrice) || 0,
      lifetimePrice: Number(initialData?.lifetimePrice) || 0,
    };

    // Only keep the selected duration price for create; on edit update that duration's price.
    if (!isEdit) {
      prices.monthlyPrice = 0;
      prices.quarterlyPrice = 0;
      prices.yearlyPrice = 0;
      prices.lifetimePrice = 0;
    }

    if (form.duration === 'monthly') prices.monthlyPrice = price;
    if (form.duration === 'quarterly') prices.quarterlyPrice = price;
    if (form.duration === 'yearly') prices.yearlyPrice = price;
    if (form.duration === 'lifetime') prices.lifetimePrice = price;

    onSubmit?.({
      name: form.name,
      ...prices,
      trialDays: 0,
      sortOrder: Number(initialData?.sortOrder) || 0,
      status: form.status || 'active',
      features: form.features || '',
      duration: form.duration,
      price,
    });
  };

  return (
    <form className="subscription-form" onSubmit={handleSubmit}>
      <div className="owner-form-group">
        <label className="owner-form-label" htmlFor="plan-name">Package Name</label>
        <input
          id="plan-name"
          name="name"
          className="owner-form-input"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g. Professional"
          required
        />
      </div>

      <div className="subscription-price-grid">
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="plan-duration">Duration</label>
          <select
            id="plan-duration"
            name="duration"
            className="owner-form-select"
            value={form.duration}
            onChange={handleChange}
            required
          >
            <option value="">Select duration...</option>
            {durationOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {form.duration && (
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="plan-price">
              {selectedDuration ? `${selectedDuration.label} Price` : 'Price'}
            </label>
            <input
              id="plan-price"
              name="price"
              type="number"
              min="0"
              step="0.01"
              className="owner-form-input"
              value={form.price}
              onChange={handleChange}
              placeholder="0.00"
              required
            />
          </div>
        )}
      </div>

      <div className="owner-form-group">
        <label className="owner-form-label" htmlFor="plan-status">Status</label>
        <select
          id="plan-status"
          name="status"
          className="owner-form-select"
          value={form.status}
          onChange={handleChange}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="owner-form-group">
        <label className="owner-form-label" htmlFor="plan-features">Features</label>
        <textarea
          id="plan-features"
          name="features"
          className="owner-form-input owner-form-textarea"
          value={form.features}
          onChange={handleChange}
          placeholder="Enter features (one per line)&#10;e.g. Up to 500 members&#10;Unlimited branches"
          rows={6}
          required
        />
      </div>

      {isEdit && (
        <p className="settings-logo-hint owner-mb-16">
          Plan ID: <code>{initialData?.id}</code> — used when gyms sign up.
        </p>
      )}

      <div className="subscription-form-actions">
        <button type="submit" className="owner-btn owner-btn-primary" disabled={!form.duration}>
          {submitLabel}
        </button>
        {onCancel ? (
          <button type="button" className="owner-btn owner-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

export default SaasPlanForm;
